'use client';

import { useTimeSettings } from '@mnd-frontend/contexts';
import type { Sentiment } from '@mnd-frontend/graphql-schema';
import { MonitorMediaFilterType, MonitorSourceVerification } from '@mnd-frontend/graphql-schema';
import { Avatar, Checkbox, Icons } from '@mnd-frontend/ui/publish/components';
import { useTranslation } from '@mnd-frontend/ui/publish/contexts';
import { withLineClamp } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_200,
  GREY_900,
  GREY_1000,
  NEUTRAL_WHITE,
  RADIUS_LG,
  SIZING_1_5,
  SIZING_2,
  SPACING_0_5,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
  WARNING_100,
} from '@mnd-frontend/ui/shared/tokens';
import { localizeValue } from '@mnd-frontend/utils';
import { Fragment, type MouseEventHandler, useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';

import { formatLinkWithSeekParam } from '../utils/formatLinkWithSeekParam';

import AISummary from './AISummary';
import AudioPlayer from './AudioPlayer';
import Dropdown, { type DropdownProps } from './Dropdown';
import LiveTime from './LiveTime';
import MediaType from './MediaType';
import MentionCardAvatar from './MentionCardAvatar';

const MentionCardContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1_5};
`;

const CheckboxContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: ${SPACING_1_5};
  left: ${SPACING_1_5};
  background-color: ${NEUTRAL_WHITE};
  padding: ${SPACING_0_5};
  border-radius: ${props => props.theme.radiusCheckbox};
`;

const MentionCardHeader = styled.div<{ $compact: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_2)};
`;

const MentionCardHeaderLeft = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-grow: 1;
  align-items: end;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const MentionCardHeaderRight = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: start;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const MentionCardHeaderRightContent = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_1_5)};
`;

const SourceContainer = styled.div<{ $compact: boolean }>`
  margin-bottom: ${props => (props.$compact ? '2px' : '5px')};
`;

const SourceName = styled.h4<{ $compact: boolean }>`
  font-size: ${props => (props.$compact ? '0.875rem' : SIZING_2)};
  line-height: 1.125;
  color: ${GREY_1000};
  font-weight: 700;
  margin: ${props => (props.$compact ? '0' : '0 0 4px 0')};
  ${withLineClamp(3)}
`;

const Title = styled.h3<{ $compact: boolean }>`
  color: ${GREY_1000};
  font-weight: 700;
  font-size: ${props => (props.$compact ? '1rem' : '1.375rem')};
  line-height: 1.25;
  margin: ${props => (props.$compact ? '0 0 4px 0' : '0 0 8px 0')};
  overflow-wrap: break-word;
  ${withLineClamp(3)}
`;

const Hitline = styled.p<{ $compact: boolean }>`
  color: ${GREY_1000};
  font-weight: 400;
  font-size: ${props => (props.$compact ? SIZING_1_5 : SIZING_2)};
  line-height: 1.375;
  margin: ${props => (props.$compact ? 0 : '0 0 4px 0')};
  overflow-wrap: break-word;

  b {
    font-weight: 600;
    background-color: ${WARNING_100};
  }
`;

const MentionCardFooter = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_3)};
`;

const MetadataLink = styled(Link)`
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const MetadataExternalLink = styled.a`
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const Metadata = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING_1} ${SPACING_2};
  flex-wrap: wrap;
  font-weight: 500;
  font-size: ${props => (props.$compact ? SIZING_1_5 : '0.875rem')};
  line-height: 1;
  color: ${GREY_900};
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: end;
  gap: ${SPACING_0_5};
`;

const AvatarContainer = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  & > *:not(:first-child) {
    margin-right: ${props => (props.$compact ? '-1.5rem' : '-2rem')};
  }
`;

const ShowMultipleAudioHitlines = styled.button`
  all: unset;
  color: ${props => props.theme.colorIconsInfo};
  cursor: pointer;
`;

const AudioPlayerContainer = styled.div<{
  $compact: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? SPACING_1 : SPACING_2)};
`;

const linkText = (mediaTypeId?: MonitorMediaFilterType): string => {
  switch (mediaTypeId) {
    case MonitorMediaFilterType.Web:
    case MonitorMediaFilterType.Print:
    case MonitorMediaFilterType.Blogs:
    case MonitorMediaFilterType.Forums:
      return 'monitor_mention-card_linkText-article';
    case MonitorMediaFilterType.TvPlay:
    case MonitorMediaFilterType.TvWeb:
    case MonitorMediaFilterType.Youtube:
    case MonitorMediaFilterType.Tiktok:
      return 'monitor_mention-card_linkText-video';
    case MonitorMediaFilterType.Podcast:
    case MonitorMediaFilterType.Radio:
      return 'monitor_mention-card_linkText-audio';
    case MonitorMediaFilterType.Twitter:
    case MonitorMediaFilterType.Instagram:
    case MonitorMediaFilterType.Linkedin:
    case MonitorMediaFilterType.Facebook:
      return 'monitor_mention-card_linkText-socialMedia';
    default:
      return 'monitor_mention-card_linkText-article';
  }
};

type MentionCardHit = {
  id: string;
  reach?: number;
  audioHitlines?: Array<{
    id: string;
    startPos: number;
    endPos: number;
    hitline: string;
  }>;
  mediaType2?: {
    id: MonitorMediaFilterType;
    name: string;
  };
  title?: string | null;
  sourceName?: string | null;
  thumbnailUrl?: string | null;
  publishedAt?: string;
  publishedBy?: {
    avatarUrl?: string | null;
    name?: string | null;
    link?: string | null;
  } | null;
  brandAiFilter?: {
    analysis?: string | null;
    brandSentiment?: Sentiment | null;
    brandSentimentDesc?: string | null;
    relevanceLevel?: number | null;
  } | null;
  audioUrl?: string | null;
  hitLine?: string | null;
  link?: string | null;
  duplicates?: MentionCardHit[];
  sourceVerification?: MonitorSourceVerification;
};

type MentionCardContentProps = {
  hit: MentionCardHit;
  selected?: boolean;
  onToggleSelected?: () => void;
  showSelected?: boolean;
  compact?: boolean;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  dropdown?: DropdownProps;
  isDuplicate?: boolean;
  monitorSentiment?: boolean;
  checkSourceVerification?: boolean;
  duplicatesExpanded?: boolean;
  noPadding?: boolean;
};

const MentionCardContent = ({
  compact = false,
  onLinkClick,
  dropdown,
  hit,
  isDuplicate = false,
  monitorSentiment,
  checkSourceVerification = false,
  duplicatesExpanded = false,
}: MentionCardContentProps) => {
  const { t } = useTranslation();
  const [showMultipleAudioHitlines, setShowMultipleAudioHitlines] = useState(false);

  const {
    material: { timezone, locale },
  } = useTimeSettings();

  const {
    sourceVerification,
    reach,
    duplicates,
    audioHitlines,
    mediaType2,
    title,
    sourceName,
    thumbnailUrl,
    publishedAt,
    publishedBy,
    brandAiFilter,
    audioUrl,
    hitLine,
    link,
  } = hit;

  const firstAudioHitLine = audioHitlines?.[0];

  const { analysis, brandSentiment, brandSentimentDesc, relevanceLevel } = brandAiFilter || {};

  const shouldShowReach =
    checkSourceVerification && sourceVerification
      ? sourceVerification !== MonitorSourceVerification.Rejected
      : true;

  const localizedReach =
    reach != null && reach > 0 && localizeValue(reach, { locale: locale || 'en-US' });

  const hasDuplicates = duplicates && duplicates.length > 0;
  const hasMultipleAudioHitlines = audioHitlines && audioHitlines.length > 1;

  const isSocialMediaPost = mediaType2?.id
    ? [
        MonitorMediaFilterType.Facebook,
        MonitorMediaFilterType.Instagram,
        MonitorMediaFilterType.Twitter,
        MonitorMediaFilterType.Linkedin,
      ].includes(mediaType2?.id)
    : false;

  const name = isSocialMediaPost ? title : sourceName;

  return (
    <MentionCardContentContainer>
      <MentionCardHeader $compact={compact}>
        <MentionCardHeaderLeft $compact={compact}>
          <AvatarContainer $compact={compact}>
            {hasDuplicates &&
              !duplicatesExpanded &&
              duplicates
                .slice()
                .reverse()
                .slice(0, 4)
                .map(duplicateItem => (
                  <MentionCardAvatar
                    key={duplicateItem.id}
                    mediaFilterType={duplicateItem.mediaType2?.id}
                    thumbnailUrl={duplicateItem.thumbnailUrl}
                    compact={compact}
                  />
                ))}
            <MentionCardAvatar
              mediaFilterType={mediaType2?.id}
              thumbnailUrl={thumbnailUrl}
              compact={compact}
            />
          </AvatarContainer>
          <SourceContainer $compact={compact}>
            {name && (
              <SourceName $compact={compact}>
                {hasDuplicates && !isDuplicate && !duplicatesExpanded
                  ? `${name} + ${duplicates.length}`
                  : name}
              </SourceName>
            )}
            {mediaType2?.id && (
              <MediaType id={mediaType2?.id} name={mediaType2?.name} compact={compact} />
            )}
          </SourceContainer>
        </MentionCardHeaderLeft>
        <MentionCardHeaderRight $compact={compact}>
          <MentionCardHeaderRightContent $compact={compact}>
            {publishedAt && (
              <LiveTime date={publishedAt} timezone={timezone} locale={locale} compact={compact} />
            )}
            {dropdown?.items && dropdown?.items.length > 0 && (
              <Dropdown trigger={dropdown.trigger} items={dropdown.items} />
            )}
          </MentionCardHeaderRightContent>
        </MentionCardHeaderRight>
      </MentionCardHeader>
      <div>
        {title && !isSocialMediaPost && <Title $compact={compact}>{title}</Title>}
        {audioUrl && !!audioHitlines?.length ? (
          <AudioPlayerContainer $compact={compact}>
            {audioHitlines.map((hitline, index) => (
              <AudioPlayer
                key={index}
                audioUrl={audioUrl}
                audioHitline={hitline}
                compact={compact}
                visible={showMultipleAudioHitlines ? true : index === 0}
              />
            ))}
          </AudioPlayerContainer>
        ) : (
          hitLine && <Hitline $compact={compact} dangerouslySetInnerHTML={{ __html: hitLine }} />
        )}
      </div>
      <MentionCardFooter $compact={compact}>
        <Metadata $compact={compact}>
          {hasMultipleAudioHitlines && (
            <ShowMultipleAudioHitlines
              type="button"
              onClick={() => setShowMultipleAudioHitlines(!showMultipleAudioHitlines)}
            >
              {showMultipleAudioHitlines ? (
                <MetadataItem>
                  <Icons.ChevronUp $size={compact ? 'xxxs' : 'xxs'} />
                  {t('monitor_mention-card_audio-minimize')}
                </MetadataItem>
              ) : (
                <MetadataItem>
                  <Icons.ChevronDown $size={compact ? 'xxxs' : 'xxs'} />
                  {t('monitor_mention-card_audio-mentions', {
                    count: audioHitlines?.length,
                  })}
                </MetadataItem>
              )}
            </ShowMultipleAudioHitlines>
          )}
          {link && (
            <MetadataExternalLink
              href={formatLinkWithSeekParam(link, firstAudioHitLine?.startPos)}
              onClick={onLinkClick}
              target="_blank"
              rel="noopener"
            >
              <MetadataItem>
                <Icons.ExternalLink $size={compact ? 'xxxs' : 'xxs'} />
                {t(linkText(mediaType2?.id))}
              </MetadataItem>
            </MetadataExternalLink>
          )}
          {!monitorSentiment && shouldShowReach && localizedReach && (
            <MetadataItem>
              <Icons.Reach $size={compact ? 'xxxs' : 'xxs'} />
              <span>{localizedReach}</span>
            </MetadataItem>
          )}
          {publishedBy &&
            (publishedBy.avatarUrl ? (
              publishedBy.link ? (
                <MetadataLink to={publishedBy.link}>
                  <MetadataItem>
                    <Avatar
                      type="contact"
                      size="xxs"
                      image={publishedBy.avatarUrl}
                      name={publishedBy.name || ''}
                    />
                    {publishedBy.name}
                  </MetadataItem>
                </MetadataLink>
              ) : (
                <MetadataItem>
                  <Avatar
                    type="contact"
                    size="xxs"
                    image={publishedBy.avatarUrl}
                    name={publishedBy.name || ''}
                  />
                  {publishedBy.name}
                </MetadataItem>
              )
            ) : publishedBy.link ? (
              <MetadataItem>
                {t('common_by')}{' '}
                <MetadataLink to={publishedBy.link}>{publishedBy.name}</MetadataLink>
              </MetadataItem>
            ) : (
              <MetadataItem>
                {t('common_by')} {publishedBy.name}
              </MetadataItem>
            ))}
        </Metadata>
        {!isDuplicate && (analysis || brandSentiment || relevanceLevel) && (
          <AISummary
            compact={compact}
            analysis={analysis}
            brandSentiment={brandSentiment}
            relevanceLevel={relevanceLevel}
            brandSentimentDesc={brandSentimentDesc}
          />
        )}
      </MentionCardFooter>
    </MentionCardContentContainer>
  );
};

const getPadding = (compact: boolean, hasDuplicates?: boolean) => {
  const spacing = compact ? SPACING_2 : SPACING_3;
  return hasDuplicates ? `${spacing} ${spacing} 0 ${spacing}` : spacing;
};

const MentionCardContainer = styled.div<{
  $compact: boolean;
  $hasDuplicates?: boolean;
  $noPadding?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? SPACING_2 : SPACING_3)};
  border-radius: ${RADIUS_LG};
  padding: ${({ $compact, $hasDuplicates, $noPadding }) =>
    $noPadding ? '0' : getPadding($compact, $hasDuplicates)};
  background-color: ${NEUTRAL_WHITE};
`;

const ToggleDuplicatesButton = styled.button<{ $compact: boolean }>`
  display: flex;
  height: 48px;
  gap: 8px;
  align-items: center;
  justify-content: center;
  line-height: 0;
  outline: none;
  background-color: transparent;
  border: 0;
  border-top: 2px solid ${GREY_200};
  padding: 0;
  margin-left: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  margin-right: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  font-size: ${props => (props.$compact ? SIZING_1_5 : '14px')};
  font-weight: 500;
  color: ${GREY_1000};
`;

const Divider = styled.div<{ $compact: boolean }>`
  border-top: 2px solid ${GREY_200};
  margin-left: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
  margin-right: -${props => (props.$compact ? SPACING_2 : SPACING_3)};
`;

const MentionCard = (props: MentionCardContentProps) => {
  const [duplicatesExpanded, setDuplicatesExpanded] = useState(false);
  const { hit, compact = false, noPadding = false } = props;
  const { t } = useTranslation();
  const duplicates = hit.duplicates;
  const hasDuplicates = duplicates && duplicates.length > 0;

  return (
    <MentionCardContainer $compact={compact} $hasDuplicates={hasDuplicates} $noPadding={noPadding}>
      {props.showSelected && props.onToggleSelected && (
        <CheckboxContainer>
          <Checkbox onChange={props.onToggleSelected} checked={!!props.selected} />
        </CheckboxContainer>
      )}
      <MentionCardContent {...props} compact={compact} duplicatesExpanded={duplicatesExpanded} />
      {hasDuplicates &&
        duplicatesExpanded &&
        duplicates.map(duplicate => (
          <Fragment key={duplicate.id}>
            <Divider $compact={compact} />
            <MentionCardContent hit={duplicate} compact={compact} isDuplicate />
          </Fragment>
        ))}
      {hasDuplicates && (
        <ToggleDuplicatesButton
          onClick={() => setDuplicatesExpanded(!duplicatesExpanded)}
          $compact={compact}
          aria-expanded={duplicatesExpanded}
        >
          {duplicatesExpanded ? (
            <>
              <Icons.ChevronUp $size={compact ? 'xxxs' : 'xxs'} />
              {t('monitor_mention-card_hide-duplicates', {
                count: duplicates?.length,
              })}
            </>
          ) : (
            <>
              <Icons.ChevronDown $size={compact ? 'xxxs' : 'xxs'} />
              {t('monitor_mention-card_show-duplicates', {
                count: duplicates?.length,
              })}
            </>
          )}
        </ToggleDuplicatesButton>
      )}
    </MentionCardContainer>
  );
};

export default MentionCard;
